import {
  Box,
  Checkbox,
  InputAdornment,
  TextField,
  ToggleButton,
  Tooltip,
} from "@mui/material"
import { InvoiceIcon } from "./InvoiceIcon"
import {
  InvoiceItemHeader,
  InvoiceItemContainer,
  StyledSwitch,
  StyledToggleButtonGroup,
  InvoiceItemHeaderContent,
  InvoiceItemHeaderText,
  InvoiceItemTaxDetails,
  TdsContainer,
  TdsAmount,
  TdsLabel,
  ErrorText,
  InputHelperText,
  InvoiceItemHeaderContentText,
  InvoiceItemAmountDetails,
  InvoiceDetails,
  InvoiceItemAmountContentText,
  InvoiceItemDateContentText,
  InvoiceStatusChip,
} from "./styles"
import { formatPriceWithRupeeSymbol } from "../../utils/price-formatter"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import { useMemo } from "react"
import { Invoice } from "../../models/invoice.model"
import {
  ReconciliationFormItem,
  ReconciliationFormValues,
} from "../../models/reconciliation.model"
import { FormikProps } from "formik"
import { titleCase } from "../../utils/transactionHelpers"
import dayjs from "dayjs"
import { CreditNoteMenu } from "./CreditNoteMenu"
import { INVOICE_STATUS_CONFIG } from "../../constants/data"

export interface InvoiceItemProps {
  txnInvoiceSource: string
  isSelected: boolean
  reconciliationItem: ReconciliationFormItem | null
  formik: FormikProps<ReconciliationFormValues>
  invoice: Invoice
  handleInvoiceToggle: (invoice: Invoice, checked: boolean) => void
  handleTaxPercentToggle: (invoice: Invoice, taxPercent: number) => void
  handleAmountChange: (invoiceId: string, amount: number | "") => void
  handleTaxEnabledToggle: (invoice: Invoice, isEnabled: boolean) => void
  itemIndex: number
  isTransactionAmountConsumed: boolean
  balanceAmountCalculationText: Record<string, string>
}

export const InvoiceItem: React.FC<InvoiceItemProps> = ({
  txnInvoiceSource,
  isSelected,
  reconciliationItem,
  formik,
  invoice,
  handleInvoiceToggle,
  itemIndex,
  handleTaxPercentToggle,
  handleAmountChange,
  handleTaxEnabledToggle,
  isTransactionAmountConsumed,
  balanceAmountCalculationText,
}) => {
  const isSameSource = useMemo(
    () => invoice.source === txnInvoiceSource,
    [invoice, txnInvoiceSource],
  )

  const invoiceSourceErrorMessage = useMemo(
    () =>
      `Invoice (${titleCase(invoice.source)}) and transaction (${titleCase(
        txnInvoiceSource,
      )}) sources do not match`,
    [invoice, txnInvoiceSource],
  )

  const isBalanceAmountNegative = useMemo(() => {
    return formik.values.balanceAmount < 0
  }, [formik.values.balanceAmount])

  const handleInvoiceClick = () => {
    if (!isSameSource) {
      return
    }
    if (isSelected) {
      handleInvoiceToggle(invoice, false)
    } else if (!isTransactionAmountConsumed) {
      handleInvoiceToggle(invoice, true)
    }
  }

  const chipConfig = INVOICE_STATUS_CONFIG.find(
    (s) => s.status === invoice.status,
  )

  return (
    <InvoiceItemContainer selected={isSelected}>
      <InvoiceItemHeader selected={isSelected}>
        <Box>
          <InvoiceItemHeaderContent>
            <InvoiceItemHeaderText>
              <InvoiceIcon />
              <InvoiceItemHeaderContentText component="span">
                {invoice.invoiceNumber}{" "}
              </InvoiceItemHeaderContentText>
            </InvoiceItemHeaderText>
          </InvoiceItemHeaderContent>
          {isSameSource ? null : (
            <ErrorText>{invoiceSourceErrorMessage}</ErrorText>
          )}
        </Box>
        <CreditNoteMenu
          name="Credit Note"
          creditNotes={
            invoice?.creditNotes?.length === 0 ? [] : invoice?.creditNotes
          }
        />
        <InvoiceStatusChip
          label={chipConfig?.title}
          backgroundColor={chipConfig?.color as any}
        />
      </InvoiceItemHeader>
      {
        <InvoiceDetails>
          <InvoiceItemTaxDetails>
            <InvoiceItemAmountDetails>
              <InvoiceItemAmountContentText>
                Amount : {formatPriceWithRupeeSymbol(invoice.total)}
                {invoice.total !== invoice.balance && (
                  <>
                    {" "}
                    | Due Amount : {formatPriceWithRupeeSymbol(invoice.balance)}
                  </>
                )}
              </InvoiceItemAmountContentText>
              <InvoiceItemDateContentText>
                Start date : {dayjs(invoice.date).format("DD/MM/YYYY")} - Due
                date : {dayjs(invoice.dueDate).format("DD/MM/YYYY")}
              </InvoiceItemDateContentText>
            </InvoiceItemAmountDetails>
            {invoice.tdsAlreadyDeducted !== 0 && (
              <TdsContainer>
                <TdsLabel component="span">TDS %</TdsLabel>
                <StyledToggleButtonGroup
                  color="primary"
                  value={invoice.lastTdsPercent}
                  exclusive
                  aria-label="Platform"
                  size="small"
                >
                  {formik.values.customer?.possibleTds.map((tds) => (
                    <ToggleButton
                      value={tds}
                      size="small"
                      key={tds}
                      disabled={!invoice.canTdsDeducted([tds])}
                    >
                      {`${tds}%`}
                    </ToggleButton>
                  ))}
                </StyledToggleButtonGroup>
                <TdsAmount component="span">
                  TDS previously deducted :{" "}
                  {formatPriceWithRupeeSymbol(invoice.tdsAlreadyDeducted)}
                </TdsAmount>
              </TdsContainer>
            )}
            {invoice.isTdsFullyDeducted && (
              <TdsAmount
                component="span"
                style={{ color: "#4caf50", fontWeight: 600 }}
              >
                ✓ TDS fully deducted - No additional TDS will be applied
              </TdsAmount>
            )}
            {reconciliationItem && (
              <>
                {invoice.canTdsDeducted(invoice?.possibleTds ?? []) && (
                  <TdsContainer>
                    <TdsLabel component="span">TDS Deducted?</TdsLabel>
                    <StyledSwitch
                      component="span"
                      checked={reconciliationItem.isTdsDeducted}
                      name={`reconciliationItems.${itemIndex}.isTdsDeducted`}
                      onChange={(event, val) => {
                        handleTaxEnabledToggle(invoice, val)
                      }}
                    ></StyledSwitch>
                    {reconciliationItem.isTdsDeducted && (
                      <>
                        <TdsLabel component="span">TDS %</TdsLabel>
                        <StyledToggleButtonGroup
                          color="primary"
                          value={reconciliationItem.tdsPercentage}
                          defaultValue={invoice?.defaultTds}
                          exclusive
                          onChange={(event, val) => {
                            handleTaxPercentToggle(invoice, val)
                          }}
                          aria-label="Platform"
                          size="small"
                        >
                          {invoice?.possibleTds.map((tds) => (
                            <ToggleButton
                              value={tds}
                              size="small"
                              key={tds}
                              disabled={!invoice.canTdsDeducted([tds])}
                            >
                              {`${tds}%`}
                            </ToggleButton>
                          ))}
                        </StyledToggleButtonGroup>
                        <TdsAmount component="span">
                          Deducted TDS :{" "}
                          {formatPriceWithRupeeSymbol(
                            reconciliationItem.taxAmountWithheld,
                          )}
                        </TdsAmount>
                      </>
                    )}
                  </TdsContainer>
                )}
                <TextField
                  fullWidth
                  label="Amount"
                  type="number"
                  name={`reconciliationItems.${itemIndex}.amount`}
                  size="small"
                  value={reconciliationItem.amount}
                  sx={{ paddingBlockEnd: "4px" }}
                  onChange={(event) => {
                    if (event.target.value === "") {
                      handleAmountChange(invoice.invoiceId, "")
                    } else {
                      handleAmountChange(
                        invoice.invoiceId,
                        Number.parseFloat(event.target.value),
                      )
                    }
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    (formik as any).errors.reconciliationItems?.[itemIndex]
                      ?.amount &&
                    Boolean(
                      (formik as any).errors.reconciliationItems?.[itemIndex]
                        ?.amount,
                    )
                  }
                  helperText={
                    (formik as any).errors.reconciliationItems?.[itemIndex]
                      ?.amount &&
                    (formik as any).errors.reconciliationItems?.[itemIndex]
                      ?.amount
                  }
                  InputProps={{
                    endAdornment:
                      isBalanceAmountNegative &&
                      Boolean(
                        balanceAmountCalculationText[invoice.invoiceId],
                      ) ? (
                        <InputAdornment position="start">
                          <InputHelperText>
                            {balanceAmountCalculationText[invoice.invoiceId]}
                          </InputHelperText>
                        </InputAdornment>
                      ) : null,
                  }}
                />
              </>
            )}
          </InvoiceItemTaxDetails>
          {isSameSource ? (
            <Checkbox
              size="small"
              disabled={isTransactionAmountConsumed && !isSelected}
              checked={isSelected}
              onChange={handleInvoiceClick}
            />
          ) : (
            <Tooltip title={invoiceSourceErrorMessage}>
              <InfoOutlinedIcon color="error" />
            </Tooltip>
          )}
        </InvoiceDetails>
      }
    </InvoiceItemContainer>
  )
}
